"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import type { SearchEntityResult } from "fansunited-data-layer";
import type { FUSportsEventDetails } from "../data/types";
import { BrandedMatchHeader } from "@/components/branded/headers/BrandedMatchHeader";
import { TeamLogo } from "@/components/ui/TeamLogo";
import type { ResolvedHeaderColors } from "@/features/theme/resolveBrandedStyle";
import { useLabels, useLocale } from "@/lib/labels/LabelsProvider";
import { getAggregateScore, resolvePostRegulationCode } from "../utils/sportShape";
import type { SportEventSport } from "../utils/sportShape";
import { getEntityBrandColor, getEntityCountryCode, getEntityCountryFlag, getEntityLogo } from "../utils/entityHelpers";
interface Props {
  details: FUSportsEventDetails;
  entities: Record<string, SearchEntityResult>;
  sport: SportEventSport;
  headerColors?: ResolvedHeaderColors;
  breadcrumbs?: React.ReactNode;
}
export function SportEventHeader(t0) {
  const $ = _c(80);
  const {
    details,
    entities,
    sport,
    headerColors,
    breadcrumbs
  } = t0;
  const locale = useLocale();
  const labels = useLabels();
  const t = labels.sportEventPage;
  const {
    event
  } = details.data;
  const one = entities[event.competitorOne.id];
  const two = entities[event.competitorTwo.id];
  const competition = entities[event.competitionId];
  const isLive = event.status.type === "LIVE";
  const isFinished = event.status.type === "FINISHED";
  let t1;
  if ($[0] !== event.score) {
    t1 = getAggregateScore(event.score);
    $[0] = event.score;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const aggregate = t1;
  const postRegCode = isFinished ? resolvePostRegulationCode(event.winner?.reason) : null;
  const postRegShort = postRegCode ? labels.matchPage.status[postRegCode] : null;
  const postRegLong = postRegCode ? labels.matchPage.status[`${postRegCode}Full` as const] : null;
  let t2;
  if ($[2] !== event.timing.startTime) {
    t2 = event.timing.startTime ? new Date(event.timing.startTime) : null;
    $[2] = event.timing.startTime;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const kickoff = t2;
  let t3;
  bb0: {
    if (!kickoff || Number.isNaN(kickoff.getTime())) {
      t3 = null;
      break bb0;
    }
    let t4;
    if ($[4] !== kickoff || $[5] !== locale) {
      t4 = kickoff.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      });
      $[4] = kickoff;
      $[5] = locale;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== kickoff || $[8] !== locale) {
      t5 = kickoff.toLocaleDateString(locale, {
        weekday: "short",
        month: "short",
        day: "numeric"
      });
      $[7] = kickoff;
      $[8] = locale;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== t4 || $[11] !== t5) {
      t6 = {
        time: t4,
        date: t5
      };
      $[10] = t4;
      $[11] = t5;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    t3 = t6;
  }
  const kickoffText = t3;
  const logoKind = sport === "tennis" ? "player" : "team";
  const t4 = headerColors?.colors;
  const t5 = headerColors?.backgroundImageUrl;
  let t6;
  if ($[13] !== competition || $[14] !== event.round || $[15] !== labels.matches) {
    t6 = competition?.name ? <div className="flex items-center justify-center gap-2 text-xs [color:var(--header-text-color-muted)]">{getEntityLogo(competition) && <TeamLogo src={getEntityLogo(competition)} kind="competition" size="xs" alt="" competitionId={competition.id} />}<span>{competition.name}</span>{event.round?.matchday != null && <><span className="opacity-50">•</span><span>{labels.matches.round} {event.round.matchday}</span></>}</div> : null;
    $[13] = competition;
    $[14] = event.round;
    $[15] = labels.matches;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== one) {
    t7 = getEntityLogo(one) || "";
    $[17] = one;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== one) {
    t8 = getEntityLogo(one);
    $[19] = one;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== one) {
    t9 = getEntityCountryCode(one);
    $[21] = one;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== one) {
    t10 = getEntityCountryFlag(one);
    $[23] = one;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== one) {
    t11 = getEntityBrandColor(one, "primary");
    $[25] = one;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== one) {
    t12 = getEntityBrandColor(one, "secondary");
    $[27] = one;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== logoKind || $[30] !== t10 || $[31] !== t11 || $[32] !== t12 || $[33] !== t8 || $[34] !== t9) {
    t13 = <TeamLogo src={t8} size="xl" kind={logoKind} countryCode={t9} countryFlag={t10} primaryColor={t11} secondaryColor={t12} />;
    $[29] = logoKind;
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
    $[33] = t8;
    $[34] = t9;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const t14 = one?.name ?? t.competitorFallbackOne;
  let t15;
  if ($[36] !== t13 || $[37] !== t14 || $[38] !== t7) {
    t15 = {
      imageUrl: t7,
      image: t13,
      name: t14
    };
    $[36] = t13;
    $[37] = t14;
    $[38] = t7;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== two) {
    t16 = getEntityLogo(two) || "";
    $[40] = two;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== two) {
    t17 = getEntityLogo(two);
    $[42] = two;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== two) {
    t18 = getEntityCountryCode(two);
    $[44] = two;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== two) {
    t19 = getEntityCountryFlag(two);
    $[46] = two;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== two) {
    t20 = getEntityBrandColor(two, "primary");
    $[48] = two;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== two) {
    t21 = getEntityBrandColor(two, "secondary");
    $[50] = two;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== logoKind || $[53] !== t17 || $[54] !== t18 || $[55] !== t19 || $[56] !== t20 || $[57] !== t21) {
    t22 = <TeamLogo src={t17} size="xl" kind={logoKind} countryCode={t18} countryFlag={t19} primaryColor={t20} secondaryColor={t21} />;
    $[52] = logoKind;
    $[53] = t17;
    $[54] = t18;
    $[55] = t19;
    $[56] = t20;
    $[57] = t21;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  const t23 = two?.name ?? t.competitorFallbackTwo;
  let t24;
  if ($[59] !== t16 || $[60] !== t22 || $[61] !== t23) {
    t24 = {
      imageUrl: t16,
      image: t22,
      name: t23
    };
    $[59] = t16;
    $[60] = t22;
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== aggregate || $[64] !== isFinished || $[65] !== isLive || $[66] !== kickoffText?.date || $[67] !== kickoffText?.time || $[68] !== postRegLong || $[69] !== postRegShort || $[70] !== t) {
    t25 = <div className="text-center mx-6">{isLive || isFinished ? <><div className="text-3xl font-bold [color:var(--header-text-color)]">{aggregate ? `${aggregate.one} - ${aggregate.two}` : "0 - 0"}</div><div className="text-xs [color:var(--header-text-color-muted)]">{isLive ? t.status.live : postRegShort ? <span title={postRegLong ?? undefined}>{t.status.ft} · {postRegShort}</span> : t.status.ft}</div></> : <><div className="text-xl font-bold [color:var(--header-text-color)]">{kickoffText?.time ?? ""}</div><div className="text-sm [color:var(--header-text-color-muted)]">{kickoffText?.date ?? ""}</div></>}</div>;
    $[63] = aggregate;
    $[64] = isFinished;
    $[65] = isLive;
    $[66] = kickoffText?.date;
    $[67] = kickoffText?.time;
    $[68] = postRegLong;
    $[69] = postRegShort;
    $[70] = t;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  let t26;
  if ($[72] !== breadcrumbs || $[73] !== t15 || $[74] !== t24 || $[75] !== t25 || $[76] !== t4 || $[77] !== t5 || $[78] !== t6) {
    t26 = <BrandedMatchHeader colors={t4} backgroundImageUrl={t5} breadcrumbs={breadcrumbs} topMeta={t6} leftTeam={t15} rightTeam={t24} centerContent={t25} />;
    $[72] = breadcrumbs;
    $[73] = t15;
    $[74] = t24;
    $[75] = t25;
    $[76] = t4;
    $[77] = t5;
    $[78] = t6;
    $[79] = t26;
  } else {
    t26 = $[79];
  }
  return t26;
}